/*
 * REPLACES assets/product-inventory.js
 *
 * Adds two inventory levels to the theme's existing display: `supplier` and
 * `supplier_low`. Paste over the current file in a DUPLICATED DEV THEME, never
 * the live one. Pair it with the matching snippets/product-inventory.liquid.
 *
 * WHY THIS FILE HAD TO CHANGE
 * ---------------------------
 * The original logic reads:
 *
 *     if (count <= 0) {
 *       if (inventoryPolicy === 'continue') level = 'backordered';
 *       else if (available)                 level = 'in_stock';
 *
 * A supplier-stock variant is count 0 (once the Liquid subtracts the supplier's
 * share), policy 'deny', and available true — because Shopify genuinely can
 * ship it. That lands on `in_stock`, and the bar code then does:
 *
 *     if ((count >= scale || level === 'in_stock') && level !== 'backordered')
 *       newWidth = 100;
 *
 * So without this change a product sitting in a supplier's warehouse would
 * render "In stock" with a FULL bar. Worse than showing nothing.
 *
 * The supplier levels are checked BEFORE that branch, and they deliberately
 * hide the depth bar: a full bar would say "lots left", which is true of the
 * supplier's shelf and irrelevant to us.
 *
 * A third level, `preorder`, covers stock we have already ordered and that is
 * on its way to our warehouse — Karen's Timesact products. It is tested FIRST,
 * before supplier: if both are somehow true, the goods we have already bought
 * and can date beat a supplier's shelf we would still have to order from.
 *
 * Everything else is unchanged. A variant we hold stock for behaves exactly as
 * it does today.
 */
if (!customElements.get('product-inventory')) {
  class ProductInventory extends HTMLElement {
    constructor() {
      super();
      if (this.hidden) {
        this.initLazySection();
      } else {
        window.initLazyScript(this, this.initLazySection.bind(this));
      }
    }

    initLazySection() {
      this.inventoryNotice = this.querySelector('.js-inventory-notice');
      this.urgencyMessage = this.querySelector('.js-inventory-urgency');
      this.indicatorBar = this.querySelector('.js-inventory-indicator');
      this.variantInventory = this.getVariantInventory();

      // Bind to on:variant:change event for this product
      const variantPicker = this.closest('.product-info').querySelector('variant-picker');
      if (variantPicker) {
        variantPicker.addEventListener('on:variant:change', this.handleVariantChange.bind(this));
      }

      // Init
      this.updateInventory(
        parseInt(this.dataset.inventoryQuantity, 10),
        this.dataset.variantAvailable === 'true',
        this.dataset.inventoryPolicy,
        parseInt(this.dataset.supplierQuantity, 10) || 0,
        this.dataset.variantPreorder === 'true',
        this.dataset.variantPreorderDate || ''
      );
    }

    /**
     * Gets the inventory data for all product variants
     * @returns {?object}
     */
    getVariantInventory() {
      const dataEl = this.querySelector('[type="application/json"]');
      return this.variantInventory || JSON.parse(dataEl.textContent);
    }

    /**
     * Handles a 'change' event on the variant picker element
     * @param {object} evt - Event object
     */
    handleVariantChange(evt) {
      if (evt.detail.variant) {
        const inventory = this.variantInventory.filter(
          (variant) => variant.id === evt.detail.variant.id
        )[0];
        this.updateInventory(
          inventory.inventory_quantity,
          inventory.available,
          inventory.inventory_policy,
          inventory.supplier_quantity || 0,
          inventory.preorder === true,
          inventory.preorder_date || ''
        );
      } else {
        this.updateInventory(0, false, undefined, 0, false, '');
        this.hidden = true;
      }
    }

    /**
     * Updates the inventory notice
     * @param {number} count - OUR inventory quantity, supplier stock excluded
     * @param {boolean} available - whether current variant is available
     * @param {string} inventoryPolicy - whether product continues selling when out of stock
     * @param {number} supplierQty - quantity the supplier holds, 0 if stale or none
     * @param {boolean} preorder - Timesact governs this variant: already ordered, inbound
     * @param {string} preorderDate - arrival date, pre-formatted by Liquid, '' if unknown
     */
    updateInventory(count, available, inventoryPolicy, supplierQty, preorder, preorderDate) {
      if (count === 0 && !available && !supplierQty && !preorder && this.hidden) {
        return;
      }

      const supplier = supplierQty || 0;
      const supplierIn = parseInt(this.dataset.supplierThresholdIn, 10) || 10;
      const supplierLow = parseInt(this.dataset.supplierThresholdLow, 10) || 5;
      let inventoryLevel;

      if (count <= 0 && preorder) {
        // First, on purpose. Stock we have already bought and can put a date on
        // outranks a supplier's shelf, and outranks the generic 'backordered'
        // the theme would otherwise show over the top of Timesact's own widget.
        inventoryLevel = 'preorder';
      } else if (count <= 0 && supplier >= supplierIn) {
        // Checked before the original count<=0 branch on purpose: that branch
        // would classify this as 'in_stock' and draw a full bar.
        inventoryLevel = 'supplier';
      } else if (count <= 0 && supplier >= supplierLow) {
        inventoryLevel = 'supplier_low';
      } else if (count <= 0) {
        if (inventoryPolicy === 'continue') {
          inventoryLevel = 'backordered';
        } else if (available) {
          inventoryLevel = 'in_stock';
        } else {
          inventoryLevel = 'none';
        }
      } else if (count <= parseInt(this.dataset.thresholdVeryLow, 10)) {
        inventoryLevel = 'very_low';
      } else if (count <= parseInt(this.dataset.thresholdLow, 10)) {
        inventoryLevel = 'low';
      } else {
        inventoryLevel = 'normal';
      }

      const isSupplier = inventoryLevel === 'supplier' || inventoryLevel === 'supplier_low';
      const isPreorder = inventoryLevel === 'preorder';

      // Supplier states always show. They are the whole point, and suppressing
      // them under a "only show when low" setting would leave the customer with
      // no explanation for a longer dispatch time.
      if (isSupplier || isPreorder
        || ((this.dataset.showNotice === 'always'
        || (this.dataset.showNotice === 'low' && inventoryLevel.includes('low')))
        && !(inventoryLevel === 'backordered' && this.dataset.showNoStockBackordered === 'false'))) {
        this.hidden = false;

        // Set the inventory level data attribute
        this.setAttribute('data-inventory-level', inventoryLevel);

        // Determine whether to show the count or not
        if (isPreorder) {
          this.inventoryNotice.innerText = this.dataset.labelPreorder;
        } else if (inventoryLevel === 'supplier') {
          this.inventoryNotice.innerText = this.dataset.labelSupplier;
        } else if (inventoryLevel === 'supplier_low') {
          this.inventoryNotice.innerText = this.dataset.labelSupplierLow;
        } else if (inventoryLevel === 'backordered') {
          this.inventoryNotice.innerText = '';
        } else if (inventoryLevel !== 'in_stock' && (this.dataset.showCount === 'always' || (this.dataset.showCount === 'low' && inventoryLevel.includes('low')))) {
          this.inventoryNotice.innerText = theme.strings.onlyXLeft.replace('[quantity]', count);
        } else if (inventoryLevel === 'very_low') {
          this.inventoryNotice.innerText = theme.strings.veryLowStock;
        } else if (inventoryLevel === 'low') {
          this.inventoryNotice.innerText = theme.strings.lowStock;
        } else if (inventoryLevel === 'normal' || inventoryLevel === 'in_stock') {
          this.inventoryNotice.innerText = theme.strings.inStock;
        } else if (inventoryLevel === 'none') {
          this.inventoryNotice.innerText = theme.strings.noStock;
        }

        // Update urgency message if needed
        if (this.urgencyMessage) {
          if (isPreorder) {
            this.urgencyMessage.innerHTML = preorderDate
              ? this.dataset.textPreorderDated.replace('[date]', preorderDate)
              : this.dataset.textPreorder;
          } else if (isSupplier) {
            this.urgencyMessage.innerHTML = this.dataset.textSupplier;
          } else if (inventoryLevel === 'very_low') {
            this.urgencyMessage.innerHTML = this.dataset.textVeryLow;
          } else if (inventoryLevel === 'low') {
            this.urgencyMessage.innerHTML = this.dataset.textLow;
          } else if (inventoryLevel === 'backordered') {
            this.urgencyMessage.innerHTML = this.dataset.textNoStockBackordered;
          } else if (inventoryLevel === 'normal' || inventoryLevel === 'in_stock') {
            this.urgencyMessage.innerHTML = this.dataset.textOwn || this.dataset.textNormal;
          } else if (inventoryLevel === 'none') {
            this.urgencyMessage.innerHTML = this.dataset.textNoStock;
          }
        }

        // Update the bar indicator
        if (this.indicatorBar) {
          if (isSupplier || isPreorder) {
            // No bar for supplier stock. A depth bar measures OUR shelf; drawing
            // one from a supplier's quantity is the same lie this change exists
            // to remove.
            this.indicatorBar.hidden = true;
          } else {
            this.indicatorBar.hidden = false;

            let newWidth;
            if ((count >= this.dataset.scale || inventoryLevel === 'in_stock') && inventoryLevel !== 'backordered') {
              newWidth = 100;
            } else {
              newWidth = ((100 / parseInt(this.dataset.scale, 10)) * count).toFixed(1);
            }
            this.indicatorBar.querySelector('span').style.width = `${newWidth}%`;
          }
        }
      } else {
        this.hidden = true;
      }
    }
  }

  customElements.define('product-inventory', ProductInventory);
}